import React, { useState } from 'react';
import {
  Search, User, Plus, Trash2, Edit2, Shield,
  LayoutGrid, Users, FileText, ChevronDown,
  ChevronRight, Check, Mail, Calendar, ChevronLeft,
  ShieldCheck, Lock, AlertTriangle, PlusCircle
} from 'lucide-react';

const RolePermission = () => {
  const predefinedRoles = ['Organization Admin', 'Super Admin'];

  const [customRoles, setCustomRoles] = useState([
    'KWT Admin',
    'New Custom Role',
    'New Role'
  ]);

  const [activeRole, setActiveRole] = useState('KWT Admin');
  const [activeTab, setActiveTab] = useState('Role & Permission');
  const [expandedGroups, setExpandedGroups] = useState(['projects', 'users', 'reports']);

  const [isEditing, setIsEditing] = useState(false);
  const [editingRoleName, setEditingRoleName] = useState('');
  
  // NEW STATE: Tracks the role scheduled for deletion. If null, modal is closed.
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Mock data for the Members Table
  const membersList = [
    { id: '#1042', name: 'Vaibhav Gautam', initials: 'VG', role: 'KWT Admin', status: 'Inactive', date: '02/12/2025', time: '15:13:04' },
    { id: '#1043', name: 'Vinod Gupta', initials: 'VG', role: 'KWT Admin', status: 'Active', date: '01/12/2025', time: '11:09:40' },
    { id: '#1044', name: 'Vaibhav Gautam', initials: 'VG', role: 'KWT Admin', status: 'Active', date: '28/11/2025', time: '13:36:53' },
    { id: '#1045', name: 'Arpit Sir', initials: 'AS', role: 'KWT Admin', status: 'Active', date: '28/11/2025', time: '13:33:06' },
    { id: '#1046', name: 'Srwr Alam', initials: 'SA', role: 'KWT Admin', status: 'Active', date: '28/11/2025', time: '12:08:02' },
    { id: '#1047', name: 'Arpit Sir', initials: 'AS', role: 'KWT Admin', status: 'Active', date: '28/11/2025', time: '11:49:35' },
  ];

  const [allPermissions, setAllPermissions] = useState({
    'Super Admin': {
      projects: { view: true, create: true, edit: true },
      users: { view: true, create: true, edit: true },
      reports: { view: true, create: true, edit: true }
    },
    'Organization Admin': {
      projects: { view: true, create: true, edit: true },
      users: { view: true, create: true, edit: false },
      reports: { view: true, create: true, edit: false }
    },
    'KWT Admin': {
      projects: { view: true, create: true, edit: true },
      users: { view: true, create: true, edit: true },
      reports: { view: true, create: false, edit: false }
    },
    'New Custom Role': {
      projects: { view: true, create: false, edit: false },
      users: { view: false, create: false, edit: false },
      reports: { view: false, create: false, edit: false }
    },
    'New Role': {
      projects: { view: false, create: false, edit: false },
      users: { view: false, create: false, edit: false },
      reports: { view: false, create: false, edit: false }
    }
  });

  const defaultPerms = {
    projects: { view: false, create: false, edit: false },
    users: { view: false, create: false, edit: false },
    reports: { view: false, create: false, edit: false }
  };

  const currentPermissions = allPermissions[activeRole] || defaultPerms;

  const toggleGroup = (group) => {
    setExpandedGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    );
  };

  const handleCheckboxChange = (group, action) => {
    if (!isEditing) return;

    setAllPermissions(prev => ({
      ...prev,
      [activeRole]: {
        ...prev[activeRole],
        [group]: {
          ...prev[activeRole][group],
          [action]: !prev[activeRole][group][action]
        }
      }
    }));
  };

  const handleGrantAllChange = (group) => {
    setAllPermissions(prev => {
      const currentGroup = prev[activeRole][group];
      const allChecked = currentGroup.view && currentGroup.create && currentGroup.edit;
      return {
        ...prev,
        [activeRole]: {
          ...prev[activeRole],
          [group]: { view: !allChecked, create: !allChecked, edit: !allChecked }
        }
      };
    });
  };

  const switchRole = (roleName) => {
    if (activeRole === '__NEW_DRAFT_ROLE__') {
      setAllPermissions(prev => {
        const newPerms = { ...prev };
        delete newPerms['__NEW_DRAFT_ROLE__'];
        return newPerms;
      });
    }
    setActiveRole(roleName);
    setIsEditing(false);
  };

  const handleCreateNewRole = () => {
    const tempId = '__NEW_DRAFT_ROLE__';

    setAllPermissions(prev => ({
      ...prev,
      [tempId]: {
        projects: { view: false, create: false, edit: false },
        users: { view: false, create: false, edit: false },
        reports: { view: false, create: false, edit: false }
      }
    }));

    setActiveTab('Role & Permission');
    setActiveRole(tempId);
    setEditingRoleName('Untitled Role');
    setIsEditing(true);
  };

  // MODIFIED: Now just opens the modal instead of window.confirm
  const confirmDeleteRole = () => {
    setRoleToDelete(activeRole);
  };

  // NEW: Actually executes the deletion
  const executeDeleteRole = () => {
    if (!roleToDelete) return;
    
    setCustomRoles(prev => prev.filter(r => r !== roleToDelete));
    setAllPermissions(prev => {
      const newPerms = { ...prev };
      delete newPerms[roleToDelete];
      return newPerms;
    });
    
    setActiveRole(predefinedRoles[0]);
    setIsEditing(false);
    setRoleToDelete(null); // Close modal
  };

  const startEditing = () => {
    setEditingRoleName(activeRole);
    setIsEditing(true);
  };

  const saveEdits = () => {
    const trimmedName = editingRoleName.trim();
    if (!trimmedName) return;

    if (trimmedName !== activeRole && (customRoles.includes(trimmedName) || predefinedRoles.includes(trimmedName))) {
      alert("This role name already exists. Please choose a different name.");
      return;
    }

    if (activeRole === '__NEW_DRAFT_ROLE__') {
      setCustomRoles(prev => [...prev, trimmedName]);
      setAllPermissions(prev => {
        const newPerms = { ...prev, [trimmedName]: prev['__NEW_DRAFT_ROLE__'] };
        delete newPerms['__NEW_DRAFT_ROLE__'];
        return newPerms;
      });
      setActiveRole(trimmedName);
    } else if (trimmedName !== activeRole) {
      setCustomRoles(prevRoles =>
        prevRoles.map(role => role === activeRole ? trimmedName : role)
      );
      setAllPermissions(prev => {
        const newPerms = { ...prev, [trimmedName]: prev[activeRole] };
        delete newPerms[activeRole];
        return newPerms;
      });
      setActiveRole(trimmedName);
    }

    setIsEditing(false);
  };

  const cancelEdits = () => {
    if (activeRole === '__NEW_DRAFT_ROLE__') {
      setAllPermissions(prev => {
        const newPerms = { ...prev };
        delete newPerms['__NEW_DRAFT_ROLE__'];
        return newPerms;
      });
      setActiveRole(customRoles.length > 0 ? customRoles[0] : predefinedRoles[0]);
    }
    setIsEditing(false);
  };

  // --- MODERN CHECKBOX UI ---
  const CustomCheckbox = ({ label, checked, onChange, isEditMode = false }) => {
    const handleClick = (e) => {
        e.stopPropagation();
        if (isEditMode && onChange) {
            onChange();
        }
    };

    return (
      <div 
        onClick={handleClick}
        className={`flex items-center gap-3 select-none transition-all duration-200 ${isEditMode ? 'cursor-pointer group' : 'cursor-not-allowed opacity-60'}`}
      >
        <div className={`w-5 h-5 rounded-[6px] flex items-center justify-center transition-all duration-200 ${
            checked 
            ? 'bg-indigo-600 ring-0 text-white shadow-sm shadow-indigo-600/30' 
            : `bg-slate-50 ring-1 ring-inset ring-slate-300 ${isEditMode ? 'group-hover:ring-indigo-400 group-hover:bg-indigo-50/50' : ''} text-transparent`
          }`}>
          <Check className={`w-3.5 h-3.5 stroke-[3] ${checked ? 'text-white' : 'text-transparent'}`} />
        </div>
        <span className={`text-sm font-semibold transition-colors ${checked ? 'text-slate-900' : 'text-slate-500'}`}>{label}</span>
      </div>
    );
  };

  const permissionSections = [
    { id: 'projects', label: 'Project Management', icon: LayoutGrid, desc: 'Control access to create, edit, or view projects.' },
    { id: 'users', label: 'User Directory', icon: Users, desc: 'Manage organization members and their roles.' },
    { id: 'reports', label: 'Compliance Reports', icon: FileText, desc: 'Access to generated reports and analytics.' },
  ];

  const isActiveRolePredefined = predefinedRoles.includes(activeRole);

  // --- Pagination Logic ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const totalPages = Math.ceil(membersList.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMembers = membersList.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className=" bg-slate-50  relative">
      
      {/* --- CONFIRMATION MODAL OVERLAY --- */}
      {roleToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={() => setRoleToDelete(null)}></div>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="p-6 sm:p-8 text-center">
                    <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-5 ring-8 ring-rose-50/50">
                        <AlertTriangle size={32} strokeWidth={2} />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Role?</h3>
                    <p className="text-sm font-medium text-slate-500 leading-relaxed">
                        Are you sure you want to delete the <span className="font-bold text-slate-800">"{roleToDelete}"</span> role? This action cannot be undone and any users currently assigned to this role will lose their permissions.
                    </p>
                </div>
                <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                    <button 
                        onClick={() => setRoleToDelete(null)}
                        className="flex-1 px-4 py-3 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={executeDeleteRole}
                        className="flex-1 px-4 py-3 text-sm font-bold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-md shadow-rose-600/20 active:scale-95 transition-all"
                    >
                        Yes, Delete Role
                    </button>
                </div>
            </div>
        </div>
      )}


        {/* --- HERO HEADER --- */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white hidden sm:block">
                    <ShieldCheck size={28} strokeWidth={2} />
                </div>
                <div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Roles & Permissions</h3>
                    <p className="text-sm font-semibold text-slate-500 mt-0.5">Manage access control and operational boundaries across the platform.</p>
                </div>
            </div>
            <button
                onClick={handleCreateNewRole}
                className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
            >
                <PlusCircle size={18} /> Create New Role
            </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 flex-1 h-[calc(100vh-160px)] min-h-[600px]">

          {/* --- LEFT SIDEBAR (ROLES LIST) --- */}
          <div className="w-full lg:w-[320px] bg-white ring-1 ring-slate-200/60 rounded-[2rem] shadow-sm p-6 flex flex-col shrink-0">
            
            {/* Search */}
            <div className="relative mb-8">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search roles..."
                className="w-full pl-10 pr-4 py-3 bg-slate-50 ring-1 ring-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-white transition-all placeholder-slate-400"
              />
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-8 custom-scrollbar">
                {/* Predefined Roles */}
                <div>
                    <h3 className="text-[11px] font-black text-slate-400 mb-3 tracking-widest uppercase flex items-center gap-2">
                        <Lock size={12} /> System Roles
                    </h3>
                    <ul className="space-y-1.5">
                        {predefinedRoles.map(role => {
                            const isActive = activeRole === role;
                            return (
                                <li
                                    key={role}
                                    onClick={() => switchRole(role)}
                                    className={`flex items-center px-4 py-3 text-sm font-bold rounded-xl cursor-pointer transition-all ${isActive ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/50 shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <Shield className={`w-4 h-4 mr-3 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                                    {role}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Custom Roles */}
                <div>
                    <h3 className="text-[11px] font-black text-slate-400 mb-3 tracking-widest uppercase flex items-center gap-2">
                        <User size={12} /> Custom Roles
                    </h3>
                    <ul className="space-y-1.5">
                        {customRoles.map(roleName => {
                            const isActive = activeRole === roleName;
                            return (
                                <li
                                    key={roleName}
                                    onClick={() => switchRole(roleName)}
                                    className={`flex items-center px-4 py-3 text-sm font-bold rounded-xl cursor-pointer transition-all ${isActive ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/50 shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <User className={`w-4 h-4 mr-3 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                                    {roleName}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
          </div>

          {/* --- RIGHT CONTENT AREA --- */}
          <div className="flex-1 bg-white ring-1 ring-slate-200/60 rounded-[2rem] shadow-sm flex flex-col overflow-hidden">
            
            {/* Main Header & Actions */}
            <div className="p-8 pb-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8 min-h-[44px]">
                    {!isEditing ? (
                        <>
                            <div className="flex items-center gap-3">
                                <h3 className="text-2xl font-black text-slate-900 tracking-tight">{activeRole}</h3>
                                {isActiveRolePredefined && (
                                    <span className="px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-widest rounded-md ring-1 ring-slate-200/60">System</span>
                                )}
                            </div>
                            
                            {!isActiveRolePredefined && activeTab === 'Role & Permission' && (
                                <div className="flex items-center gap-2">
                                    <button onClick={startEditing} className="flex items-center gap-2 px-4 py-2 bg-white ring-1 ring-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors shadow-sm">
                                        <Edit2 className="w-4 h-4" /> Edit Role
                                    </button>
                                    <button onClick={confirmDeleteRole} className="p-2.5 bg-white ring-1 ring-slate-200 rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-200 transition-colors shadow-sm" title="Delete Role">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex flex-col sm:flex-row w-full gap-4 animate-in fade-in">
                            <input
                                type="text"
                                value={editingRoleName}
                                onChange={(e) => setEditingRoleName(e.target.value)}
                                className="flex-1 text-2xl font-black text-slate-900 bg-slate-50 ring-1 ring-slate-200 rounded-xl px-5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all placeholder-slate-300"
                                placeholder="Enter role name..."
                                autoFocus
                            />
                            <div className="flex items-center gap-2 shrink-0">
                                <button onClick={cancelEdits} className="px-5 py-3 bg-white ring-1 ring-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 font-bold text-sm transition-colors shadow-sm">
                                    Cancel
                                </button>
                                <button onClick={saveEdits} className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-bold text-sm transition-all shadow-md hover:shadow-lg active:scale-95">
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modern Segmented Tabs */}
                <div className="flex p-1.5 bg-slate-100/80 rounded-xl ring-1 ring-slate-200/50 w-fit mb-8">
                    {['Role & Permission', 'Members'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => { setActiveTab(tab); setIsEditing(false); }}
                            className={`px-6 py-2.5 text-sm font-bold rounded-lg transition-all duration-300 ${activeTab === tab
                                ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200/50'
                                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/50'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* --- TAB CONTENT AREA --- */}
            <div className="flex-1 overflow-y-auto p-8 pt-0 custom-scrollbar">
                
                {/* 1. Permissions Section */}
                {activeTab === 'Role & Permission' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        {isEditing && (
                            <div className="mb-6 p-4 bg-indigo-50 rounded-xl ring-1 ring-indigo-200/50 flex items-start gap-3">
                                <Shield className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
                                <div>
                                    <h4 className="text-sm font-bold text-indigo-900">Edit Mode Active</h4>
                                    <p className="text-xs font-medium text-indigo-700 mt-0.5">Modify the permissions below to customize what users in this role can access. Don't forget to save your changes.</p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            {permissionSections.map((section) => {
                                const Icon = section.icon;
                                const groupPerms = currentPermissions[section.id];
                                const isGrantAllChecked = groupPerms.view && groupPerms.create && groupPerms.edit;
                                const isExpanded = expandedGroups.includes(section.id);

                                return (
                                    <div key={section.id} className={`bg-white rounded-2xl transition-all duration-300 overflow-hidden ring-1 ${isExpanded ? 'ring-slate-300 shadow-md' : 'ring-slate-200/80 shadow-sm hover:ring-slate-300'}`}>
                                        
                                        {/* Accordion Header */}
                                        <div
                                            onClick={() => toggleGroup(section.id)}
                                            className={`flex items-center justify-between p-5 cursor-pointer select-none transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`p-2.5 rounded-xl transition-colors ${isExpanded ? 'bg-white shadow-sm ring-1 ring-slate-200' : 'bg-slate-100 text-slate-500'}`}>
                                                    <Icon className={`w-5 h-5 ${isExpanded ? 'text-indigo-600' : 'text-slate-500'}`} />
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-base text-slate-900">{section.label}</h4>
                                                    <p className="text-xs font-medium text-slate-500 mt-0.5 hidden sm:block">{section.desc}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-6">
                                                {isEditing && (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <CustomCheckbox
                                                            label="Grant All"
                                                            checked={isGrantAllChecked}
                                                            onChange={() => handleGrantAllChange(section.id)}
                                                            isEditMode={isEditing}
                                                        />
                                                    </div>
                                                )}
                                                <div className={`p-1.5 rounded-full transition-colors ${isExpanded ? 'bg-slate-200/50 text-slate-700' : 'text-slate-400'}`}>
                                                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Accordion Body */}
                                        <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                            <div className="overflow-hidden">
                                                <div className="px-[76px] pb-6 pt-2 flex flex-wrap gap-8">
                                                    <CustomCheckbox
                                                        label="View Records"
                                                        checked={groupPerms.view}
                                                        onChange={() => handleCheckboxChange(section.id, 'view')}
                                                        isEditMode={isEditing}
                                                    />
                                                    <CustomCheckbox
                                                        label="Create New"
                                                        checked={groupPerms.create}
                                                        onChange={() => handleCheckboxChange(section.id, 'create')}
                                                        isEditMode={isEditing}
                                                    />
                                                    <CustomCheckbox
                                                        label="Edit Existing"
                                                        checked={groupPerms.edit}
                                                        onChange={() => handleCheckboxChange(section.id, 'edit')}
                                                        isEditMode={isEditing}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 2. Members Table Section */}
                {activeTab === 'Members' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 h-full flex flex-col ring-1 ring-slate-200/60 rounded-2xl overflow-hidden">
                        <div className="flex-1 overflow-x-auto">
                            <table className="w-full text-left border-collapse whitespace-nowrap">
                                <thead>
                                    <tr className="bg-slate-50/80 border-b border-slate-200">
                                        <th className="p-4 pl-6 text-xs font-black text-slate-400 uppercase tracking-wider w-[80px]">ID</th>
                                        <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">User Details</th>
                                        <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Contact</th>
                                        <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Status</th>
                                        <th className="p-4 pr-6 text-xs font-black text-slate-400 uppercase tracking-wider text-right">Added On</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {currentMembers.map((member) => (
                                        <tr key={member.id} className="hover:bg-slate-50/50 transition-colors group cursor-default">
                                            <td className="p-4 pl-6 text-sm font-bold text-slate-500">{member.id}</td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-700 flex items-center justify-center font-bold text-sm ring-2 ring-white shadow-sm">
                                                        {member.initials}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-extrabold text-slate-900">{member.name}</div>
                                                        <div className="text-xs font-semibold text-slate-500 mt-0.5">{member.role}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <button className="text-slate-400 hover:text-indigo-600 p-2 bg-slate-50 hover:bg-indigo-50 ring-1 ring-slate-200/60 hover:ring-indigo-200 rounded-lg transition-all shadow-sm">
                                                    <Mail size={16} />
                                                </button>
                                            </td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-black uppercase tracking-wider ring-1 shadow-sm ${
                                                    member.status === 'Active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/50' : 
                                                    'bg-rose-50 text-rose-700 ring-rose-200/50'
                                                }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${member.status === 'Active' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'}`}></span>
                                                    {member.status}
                                                </span>
                                            </td>
                                            <td className="p-4 pr-6 text-right">
                                                <div className="flex flex-col items-end justify-center text-sm text-slate-600">
                                                    <span className="font-bold text-slate-800">{member.date}</span>
                                                    <span className="text-xs font-medium text-slate-400">{member.time}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Modern Pagination Footer */}
                        <div className="flex items-center justify-between px-6 py-4 bg-slate-50/50 border-t border-slate-200">
                            <div className="text-sm font-medium text-slate-500">
                                Showing <span className="font-bold text-slate-900">{indexOfFirstItem + 1}</span> to <span className="font-bold text-slate-900">{Math.min(indexOfLastItem, membersList.length)}</span> of <span className="font-bold text-slate-900">{membersList.length}</span> users
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm text-slate-600"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => paginate(i + 1)}
                                        className={`min-w-[36px] h-9 px-2 text-sm font-bold rounded-lg transition-all ${currentPage === i + 1
                                            ? 'bg-slate-900 text-white shadow-md'
                                            : 'bg-white text-slate-600 hover:bg-slate-50 ring-1 ring-slate-200 shadow-sm'
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm text-slate-600"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
          </div>
        </div>
    </div>
  );
};

export default RolePermission;